import { QUERY_KEYS } from "@/config";
import { API_ENDPOINTS } from "@/config/api.config";
import type { APIPointsUserProfile } from "@/services";
import { api } from "@/services/real/api.service";
import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useMemo } from "react";
import { useAccount } from "wagmi";

type APIPointsUserProfileRaw = Omit<APIPointsUserProfile, "points"> & {
    v2Points: APIPointsUserProfile["points"];
};

export const usePoints = () => {
    const { address } = useAccount();
    const { data, isLoading } = useQuery<APIPointsUserProfile>({
        queryKey: QUERY_KEYS.POINTS.USER(address),
        queryFn: async () => {
            if (!address) throw new Error("Wallet not connected");
            const response = await api.get<AxiosResponse<APIPointsUserProfileRaw>>(
                API_ENDPOINTS.POINTS.GET_USER_PROFILE(address),
            );
            if (response.status !== 200) throw new Error("Failed to fetch points user profile");
            const raw = response.data.data;
            return {
                ...raw,
                points: raw.v2Points,
            };
        },
        enabled: !!address,
    });

    // phased
    const v1Points = useMemo(() => data?.v1Points ?? 0, [data]);
    const points = useMemo(() => data?.points ?? [], [data]);
    const totalPoints = useMemo(() => points.reduce((acc, point) => acc + point.points, 0), [points]);

    // aggregated
    const totalPassivePoints = useMemo(
        () => points.reduce((acc, point) => acc + point.points, 0) + v1Points,
        [v1Points, points],
    );
    const totalActivePoints = useMemo(() => data?.v1Bytes ?? 0, [data]);

    return {
        isLoading,
        // Spread API response first so our derived fields win
        ...data,
        v1Points,
        points,
        totalPassivePoints,
        totalActivePoints,
        totalPoints,
    };
};
